import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker, type DayPickerProps } from 'react-day-picker';
import { ru } from 'react-day-picker/locale';

import { toIsoDate } from '../fields/parsers/date.ts';
import { useWorkCalendar } from '../fields/work-calendar.tsx';
import { cn } from '../lib/cn.ts';

const selectedDay = '[&>button]:bg-strong [&>button]:text-on-strong [&>button]:hover:bg-strong';

/**
 * Календарь: неделя с понедельника, нерабочие дни по производственному календарю — красным,
 * сегодня обведено акцентом. Стрелки, PageUp/PageDown (месяц), Home/End (неделя) — из DayPicker.
 */
export function Calendar({
  className,
  classNames,
  modifiers,
  modifiersClassNames,
  ...props
}: DayPickerProps) {
  const work = useWorkCalendar();
  return (
    <DayPicker
      locale={ru}
      weekStartsOn={1}
      showOutsideDays
      modifiers={{ dayOff: (date: Date) => work.isDayOff(toIsoDate(date)), ...modifiers }}
      modifiersClassNames={{ dayOff: 'text-danger', ...modifiersClassNames }}
      className={cn('relative p-2 text-small', className)}
      classNames={{
        months: 'flex flex-col gap-4 sm:flex-row',
        month: 'flex flex-col gap-1.5',
        month_caption: 'flex h-7 items-center justify-center',
        caption_label: 'font-display text-body font-bold first-letter:uppercase',
        nav: 'absolute inset-x-2 top-2 flex h-7 items-center justify-between',
        button_previous:
          'grid size-7 place-items-center rounded-sm text-muted hover:bg-surface-2 hover:text-text disabled:opacity-40 [&_svg]:size-4',
        button_next:
          'grid size-7 place-items-center rounded-sm text-muted hover:bg-surface-2 hover:text-text disabled:opacity-40 [&_svg]:size-4',
        month_grid: 'border-collapse',
        weekday: 'size-9 text-caption font-bold text-muted',
        day: 'size-9 p-0 text-center',
        day_button: cn(
          'size-9 rounded-md font-semibold outline-none transition-colors',
          'hover:bg-surface-2 focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-not-allowed',
        ),
        today: '[&>button]:shadow-[inset_0_0_0_1.5px_var(--g-accent)]',
        selected: selectedDay,
        range_start: selectedDay,
        range_end: selectedDay,
        range_middle: 'bg-selected [&>button]:rounded-none [&>button]:bg-transparent [&>button]:text-text',
        outside: 'opacity-40',
        disabled: 'opacity-35',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => (orientation === 'left' ? <ChevronLeft /> : <ChevronRight />),
      }}
      {...props}
    />
  );
}
